import React, { memo, useMemo } from 'react';
import { ToolCallBlock } from './ToolCallBlock';
import { ThinkingBlock } from './ThinkingBlock';
import { WorkflowTracer } from './WorkflowTracer';
import { FileCard } from './FileCard';
import { MarkdownRenderer } from './MarkdownRenderer';
import type {
  ThinkingItem,
  FileInfo,
  WorkflowTrace,
  ContentBlock
} from '../../types/contentBlock';
import './MessageContentRenderer.css';

// ─── Props ─────────────────────

export interface MessageContentRendererProps {
  /** 正文内容 */
  readonly content: string;
  /** 是否正在流式接收 */
  readonly isStreaming?: boolean;
  /** 思考/推理过程 */
  readonly thinkingItems?: readonly ThinkingItem[];
  /** 文件列表 */
  readonly files?: readonly FileInfo[];
  /** 工作流追踪 */
  readonly workflowTrace?: WorkflowTrace | null;
  /** @deprecated 旧字段，兼容历史数据 */
  readonly contentBlocks?: readonly ContentBlock[];
  /** 插入代码回调 */
  readonly onInsertCode?: (code: string, language: string) => void;
  /** 是否显示插入按钮 */
  readonly showInsertCodeBtn?: boolean;
}

// ─── 子组件 ─────────────────────

/**
 * 思考中状态渲染器（无内容时显示）
 */
const ThinkingIndicator: React.FC = memo(() => (
  <div className="thinking-indicator">
    <i className="fas fa-spinner fa-spin" />
    <span>正在思考...</span>
  </div>
));

ThinkingIndicator.displayName = 'ThinkingIndicator';

/**
 * 打字机光标组件
 */
const TypingCursor: React.FC = memo(() => (
  <span className="typing-cursor" aria-hidden="true">▌</span>
));

TypingCursor.displayName = 'TypingCursor';

/**
 * 流式输出时的简化渲染器
 * 使用纯文本 + 光标，避免 Markdown 解析的性能开销
 */
const StreamingTextRenderer: React.FC<{ readonly content: string }> = memo(({ content }) => (
  <div className="streaming-text-content">
    {content}
    <TypingCursor />
  </div>
));

StreamingTextRenderer.displayName = 'StreamingTextRenderer';

/**
 * 从文本中剥离 <think>...</think> 标签（前端兜底）
 * 防止历史消息的 content 字段仍含有未处理的 <think> 标签
 */
const THINK_TAG_REGEX_FE = /<think>[\s\S]*?<\/think>/gi;

const stripThinkTags = (text: string): string => {
  if (!text.includes('<think>')) return text;
  return text
    .replace(THINK_TAG_REGEX_FE, '')
    .replace(/^\s*\n+/, '')
    .trim();
};

/**
 * 完整内容渲染器（非流式状态）
 * ★ 使用 MarkdownRenderer 渲染，内部自动调用 CodeBlockRenderer / SvgRenderer
 */
const FullContentRenderer: React.FC<{
  readonly content: string;
  readonly isStreaming?: boolean;
  readonly onInsertCode?: (code: string, language: string) => void;
  readonly showInsertCodeBtn?: boolean;
}> = memo(({ content, isStreaming, onInsertCode, showInsertCodeBtn }) => {
  const cleanContent = useMemo(() => stripThinkTags(content), [content]);

  if (!cleanContent) return null;

  return (
    <MarkdownRenderer
      content={cleanContent}
      isStreaming={isStreaming}
      onInsertCode={onInsertCode}
      showInsertCodeBtn={showInsertCodeBtn}
    />
  );
});

FullContentRenderer.displayName = 'FullContentRenderer';

// ─── 旧数据兼容 ─────────────────────

/**
 * 从旧 contentBlocks 中提取结构化数据
 * @deprecated 仅用于兼容旧数据库记录
 */
const extractFromLegacyBlocks = (blocks: readonly ContentBlock[]): {
  thinking: readonly ThinkingItem[];
  files: readonly FileInfo[];
  workflowTrace: WorkflowTrace | null;
} => {
  const thinking: ThinkingItem[] = [];
  const files: FileInfo[] = [];
  let workflowTrace: WorkflowTrace | null = null;

  for (const block of blocks) {
    switch (block.type) {
      case 'thinking':
        if (block.thinking || block.content) {
          thinking.push({
            id: `legacy_${thinking.length}`,
            content: block.thinking ?? block.content ?? ''
          });
        }
        break;
      case 'tool_call':
        if (block.toolCall) {
          thinking.push({
            id: block.toolCall.id ?? `legacy_tool_${thinking.length}`,
            content: block.toolCall.observation ?? '',
            tool: block.toolCall.tool,
            toolInput: block.toolCall.toolInput,
            observation: block.toolCall.observation
          });
        }
        break;
      case 'file':
        if (block.file) files.push(block.file);
        break;
      case 'workflow_finished':
        if (block.workflow) workflowTrace = block.workflow;
        break;
    }
  }

  return { thinking, files, workflowTrace };
};

// ─── 辅助：拆分 thinking items 为纯思考和工具调用 ─────────────────────

/**
 * 将 thinkingItems 拆分为纯思考和工具调用两组
 * - 纯思考 → 合并到 ThinkingBlock
 * - 工具调用 → 保留单独的 ToolCallBlock
 */
const splitThinkingItems = (items: readonly ThinkingItem[]): {
  readonly pureThinking: readonly ThinkingItem[];
  readonly toolCalls: readonly ThinkingItem[];
} => {
  const pureThinking: ThinkingItem[] = [];
  const toolCalls: ThinkingItem[] = [];

  for (const item of items) {
    if (item.tool) {
      toolCalls.push(item);
    } else {
      pureThinking.push(item);
    }
  }

  return { pureThinking, toolCalls };
};

// ─── 主组件 ─────────────────────

/**
 * 消息内容渲染器
 *
 * 渲染策略：
 * 1. 思考过程 → 合并为单个 ThinkingBlock（渐变边框，max-height 滚动）
 * 2. 工具调用 → 单独的 ToolCallBlock
 * 3. 工作流追踪 → WorkflowTracer（时间轴样式）
 * 4. 正文 → 流式用 StreamingTextRenderer，完成用 FullContentRenderer
 * 5. 文件 → FileCard
 */
export const MessageContentRenderer: React.FC<MessageContentRendererProps> = memo(({
  content,
  isStreaming = false,
  thinkingItems,
  files,
  workflowTrace,
  contentBlocks,
  onInsertCode,
  showInsertCodeBtn
}) => {
  const safeContent = content ?? '';

  // 兼容旧数据
  const legacy = useMemo(() => {
    if (contentBlocks && contentBlocks.length > 0 && !thinkingItems?.length && !files?.length && !workflowTrace) {
      return extractFromLegacyBlocks(contentBlocks);
    }
    return null;
  }, [contentBlocks, thinkingItems, files, workflowTrace]);

  const effectiveThinking = thinkingItems ?? legacy?.thinking ?? [];
  const effectiveFiles = files ?? legacy?.files ?? [];
  const effectiveWorkflowTrace = workflowTrace ?? legacy?.workflowTrace ?? null;

  // ★ 拆分：纯思考 vs 工具调用
  const { pureThinking, toolCalls } = useMemo(
    () => splitThinkingItems(effectiveThinking),
    [effectiveThinking]
  );

  const hasContent = safeContent.trim().length > 0;
  const hasThinking = pureThinking.length > 0;
  const hasToolCalls = toolCalls.length > 0;
  const hasFiles = effectiveFiles.length > 0;
  const hasWorkflow = effectiveWorkflowTrace != null;
  const hasAnything = hasContent || hasThinking || hasToolCalls || hasFiles || hasWorkflow;

  // 正在流式接收 + 什么都没有 → thinking indicator
  if (isStreaming && !hasAnything) {
    return <ThinkingIndicator />;
  }

  // 什么都没有 → 空内容
  if (!hasAnything) {
    return <div className="message-empty">（空内容）</div>;
  }

  return (
    <div className="message-structured-content">
      {/* 1. 思考过程（合并为单个块） */}
      {/* {hasThinking && (
        <ThinkingBlock items={pureThinking} isStreaming={isStreaming} />
      )} */}

      {/* 2. 工具调用（单独的块） */}
      {/* {hasToolCalls && toolCalls.map(item => (
        <ToolCallBlock key={item.id} toolItem={item} isStreaming={isStreaming} />
      ))} */}

      {/* 3. 工作流追踪 */}
      {hasWorkflow && effectiveWorkflowTrace && (
        <WorkflowTracer
          workflow={effectiveWorkflowTrace}
          nodes={effectiveWorkflowTrace.nodes}
          isStreaming={isStreaming}
        />
      )}

      {/* 4. 正文 */}
      {hasContent && (
        <FullContentRenderer
          content={safeContent}
          isStreaming={isStreaming}
          onInsertCode={onInsertCode}
          showInsertCodeBtn={showInsertCodeBtn}
        />
      )}

      {/* 5. 文件 */}
      {hasFiles && (
        <div className="message-files">
          {effectiveFiles.map(file => (
            <FileCard key={file.id} file={file} />
          ))}
        </div>
      )}
    </div>
  );
});

MessageContentRenderer.displayName = 'MessageContentRenderer';